import { getAppCopy, isPurchaseCancelledMessage } from '../../../i18n';
import { APP_STORE_PRODUCT_NOT_LOADED, PRODUCT_ID, type EntitlementStatus } from '../types';
import { isIosInAppPurchasePlatform } from './detect';

type StoreModule = typeof import('capacitor-plugin-cdv-purchase');
type CdvStore = StoreModule['store'];
type CdvProduct = import('capacitor-plugin-cdv-purchase').Product;
type CdvOffer = import('capacitor-plugin-cdv-purchase').Offer;

export class PurchaseCancelledError extends Error {
  constructor() {
    super('cancelled');
    this.name = 'PurchaseCancelledError';
  }
}

let storeModulePromise: Promise<StoreModule> | null = null;
let initPromise: Promise<CdvStore> | null = null;

let purchaseDeferred: {
  resolve: () => void;
  reject: (error: Error) => void;
} | null = null;

const PRODUCT_LOAD_TIMEOUT_MS = 25_000;
const PRODUCT_POLL_INTERVAL_MS = 400;

async function loadStoreModule(): Promise<StoreModule> {
  if (!storeModulePromise) {
    storeModulePromise = import('capacitor-plugin-cdv-purchase');
  }
  return storeModulePromise;
}

function formatProductPrice(product: CdvProduct | undefined): string | undefined {
  if (!product) return undefined;
  const pricing = product.pricing;
  if (pricing?.price) return pricing.price;
  const offer = product.getOffer?.();
  const phase = offer?.pricingPhases?.[0];
  return phase?.price;
}

function getProductOffer(store: CdvStore, mod: StoreModule): CdvOffer | null {
  const { Platform } = mod;
  const product = store.get(PRODUCT_ID, Platform.APPLE_APPSTORE);
  return product?.getOffer?.() ?? null;
}

function readEntitlement(store: CdvStore, mod: StoreModule): EntitlementStatus {
  const { Platform } = mod;
  const product = store.get(PRODUCT_ID, Platform.APPLE_APPSTORE);
  const owned = store.owned(PRODUCT_ID);
  const localTx = product ? store.findInLocalReceipts(product) : undefined;
  const verifiedTx = product ? store.findInVerifiedReceipts(product) : undefined;
  let expiresAt: string | undefined;
  if (localTx && 'expirationDate' in localTx && localTx.expirationDate) {
    expiresAt = localTx.expirationDate.toISOString();
  } else if (verifiedTx?.expiryDate) {
    expiresAt = new Date(verifiedTx.expiryDate).toISOString();
  }

  return {
    tier: owned ? 'pro' : 'free',
    source: 'appstore',
    expiresAt,
    formattedPrice: formatProductPrice(product),
  };
}

function settlePurchaseSuccess(store: CdvStore): void {
  if (!purchaseDeferred) return;
  if (!store.owned(PRODUCT_ID)) return;
  purchaseDeferred.resolve();
  purchaseDeferred = null;
}

async function waitForProductOffer(store: CdvStore, mod: StoreModule): Promise<CdvOffer> {
  const existing = getProductOffer(store, mod);
  if (existing) return existing;

  try {
    await store.update();
  } catch {
    // Keep waiting — update can fail transiently on poor networks.
  }

  const afterUpdate = getProductOffer(store, mod);
  if (afterUpdate) return afterUpdate;

  return new Promise<CdvOffer>((resolve, reject) => {
    const cleanup = () => {
      window.clearTimeout(timeoutId);
      window.clearInterval(pollId);
    };

    const tryResolve = () => {
      const offer = getProductOffer(store, mod);
      if (!offer) return false;
      cleanup();
      resolve(offer);
      return true;
    };

    const timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error(APP_STORE_PRODUCT_NOT_LOADED));
    }, PRODUCT_LOAD_TIMEOUT_MS);

    store.when().productUpdated((product) => {
      if (product.id !== PRODUCT_ID) return;
      tryResolve();
    });

    const pollId = window.setInterval(() => {
      tryResolve();
    }, PRODUCT_POLL_INTERVAL_MS);
  });
}

async function ensureStoreReady(): Promise<{ store: CdvStore; mod: StoreModule }> {
  if (!isIosInAppPurchasePlatform()) {
    throw new Error(getAppCopy().billingErrors.purchaseUnavailable);
  }

  if (!initPromise) {
    initPromise = (async () => {
      const mod = await loadStoreModule();
      const { store, ProductType, Platform, LogLevel, ErrorCode } = mod;

      store.verbosity =
        process.env.NODE_ENV === 'production' ? LogLevel.WARNING : LogLevel.DEBUG;

      store.register([
        {
          id: PRODUCT_ID,
          type: ProductType.PAID_SUBSCRIPTION,
          platform: Platform.APPLE_APPSTORE,
        },
      ]);

      store.when()
        .productUpdated((product) => {
          if (product.id !== PRODUCT_ID) return;
          settlePurchaseSuccess(store);
        })
        .approved((transaction) => {
          if (transaction.products.some((p) => p.id === PRODUCT_ID)) {
            transaction.verify();
          }
        })
        .verified((receipt) => receipt.finish())
        .finished((transaction) => {
          if (!transaction.products.some((p) => p.id === PRODUCT_ID)) return;
          settlePurchaseSuccess(store);
        })
        .receiptUpdated(() => {
          settlePurchaseSuccess(store);
        });

      store.error((error) => {
        if (!purchaseDeferred) return;
        if (
          error.code === ErrorCode.PAYMENT_CANCELLED ||
          isPurchaseCancelledMessage(error.message)
        ) {
          purchaseDeferred.reject(new PurchaseCancelledError());
        } else {
          purchaseDeferred.reject(new Error(error.message));
        }
        purchaseDeferred = null;
      });

      const initErrors = await store.initialize([Platform.APPLE_APPSTORE]);
      if (initErrors.length > 0) {
        const message = initErrors.map((e) => e.message).filter(Boolean).join('; ')
          || APP_STORE_PRODUCT_NOT_LOADED;
        throw new Error(message);
      }

      try {
        await store.update();
      } catch {
        // fetchAppStoreEntitlement / purchase will retry via waitForProductOffer.
      }

      return store;
    })();
  }

  const store = await initPromise;
  const mod = await loadStoreModule();
  return { store, mod };
}

export async function prefetchIosStoreCatalog(): Promise<EntitlementStatus> {
  return fetchAppStoreEntitlement();
}

export function isIosStoreProductReady(status: EntitlementStatus): boolean {
  return Boolean(status.formattedPrice);
}

export async function fetchAppStoreEntitlement(): Promise<EntitlementStatus> {
  const { store, mod } = await ensureStoreReady();
  try {
    await waitForProductOffer(store, mod);
  } catch {
    // Return partial status (tier/owned) even when App Store metadata is slow.
  }
  return readEntitlement(store, mod);
}

export async function purchaseAppStore(): Promise<void> {
  const { store, mod } = await ensureStoreReady();

  if (store.owned(PRODUCT_ID)) return;

  const offer = await waitForProductOffer(store, mod);

  await new Promise<void>((resolve, reject) => {
    purchaseDeferred = { resolve, reject };
    const timeoutId = window.setTimeout(() => {
      if (!purchaseDeferred) return;
      purchaseDeferred.reject(new Error(getAppCopy().billingErrors.purchaseUnavailable));
      purchaseDeferred = null;
    }, 180_000);

    const clearWait = () => window.clearTimeout(timeoutId);
    const prevResolve = purchaseDeferred.resolve;
    const prevReject = purchaseDeferred.reject;
    purchaseDeferred.resolve = () => {
      clearWait();
      prevResolve();
    };
    purchaseDeferred.reject = (error) => {
      clearWait();
      prevReject(error);
    };

    void offer.order().then((result) => {
      if (
        result &&
        typeof result === 'object' &&
        'isError' in result &&
        (result as { isError?: boolean }).isError
      ) {
        const message =
          'message' in result && typeof result.message === 'string'
            ? result.message
            : getAppCopy().billingErrors.purchaseUnavailable;
        if (isPurchaseCancelledMessage(message)) {
          purchaseDeferred?.reject(new PurchaseCancelledError());
        } else {
          purchaseDeferred?.reject(new Error(message));
        }
        purchaseDeferred = null;
        clearWait();
      }
    });
  });

  const status = readEntitlement(store, mod);
  if (status.tier !== 'pro') {
    throw new Error(getAppCopy().billingErrors.purchaseUnavailable);
  }
}

export async function restoreAppStore(): Promise<boolean> {
  const { store, mod } = await ensureStoreReady();
  await store.restorePurchases();
  return readEntitlement(store, mod).tier === 'pro';
}

export async function manageAppStoreSubscription(): Promise<void> {
  const { store, mod } = await ensureStoreReady();
  const { Platform } = mod;
  const error = await store.manageSubscriptions(Platform.APPLE_APPSTORE);
  if (error && 'message' in error) {
    throw new Error(error.message);
  }
}

export interface AppStoreSubscriptionForRedeem {
  originalTransactionId: string;
}

function readOriginalTransactionId(
  localTx: Record<string, unknown> | undefined,
  verifiedTx: Record<string, unknown> | undefined,
): string | undefined {
  const candidates = [
    verifiedTx?.originalTransactionId,
    verifiedTx?.originalTransactionIdentifier,
    localTx?.originalTransactionId,
    localTx?.originalTransactionIdentifier,
    verifiedTx?.transactionId,
    localTx?.transactionId,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }
  return undefined;
}

export async function getAppStoreSubscriptionForRedeem(): Promise<AppStoreSubscriptionForRedeem | null> {
  const { store, mod } = await ensureStoreReady();
  if (!store.owned(PRODUCT_ID)) return null;
  const { Platform } = mod;
  const product = store.get(PRODUCT_ID, Platform.APPLE_APPSTORE);
  const localTx = product
    ? (store.findInLocalReceipts(product) as Record<string, unknown> | undefined)
    : undefined;
  const verifiedTx = product
    ? (store.findInVerifiedReceipts(product) as Record<string, unknown> | undefined)
    : undefined;
  const originalTransactionId = readOriginalTransactionId(localTx, verifiedTx);
  if (!originalTransactionId) return null;
  return { originalTransactionId };
}
